import React, { Component } from 'react';
import moment from 'moment';
import API from '../../utils/API';
import SaveBar from './components/SaveBar';
import DateInput from './components/DateInput';
import LocationsStep from '../../components/proposal-requests/rfpWizardSteps/LocationsStep';
import FileChooserPreview from '../../components/inputs/fileChooserPreview';
import FilesUploaderModal from '../../components/files/filesUploaderModal';

const EVENT_TYPES = [
	{ value: 'Sales Meeting',      icon: 'las la-briefcase' },
	{ value: 'Incentive Travel',   icon: 'las la-plane-departure' },
	{ value: 'Conference',         icon: 'las la-users' },
	{ value: 'Field Marketing',    icon: 'las la-bullhorn' },
	{ value: 'Seminar / Workshop', icon: 'las la-chalkboard-teacher' },
	{ value: 'Leadership Meeting', icon: 'las la-user-tie' },
	{ value: 'Customer Event',     icon: 'las la-user-friends' },
	{ value: 'Product Launch',     icon: 'las la-rocket' },
	{ value: 'Brand Activation',   icon: 'las la-bullseye' },
	{ value: 'Hospitality Event',  icon: 'las la-cocktail' },
	{ value: 'Special Event',      icon: 'las la-star' },
	{ value: 'Other',              icon: 'las la-ellipsis-h' }
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DEFAULT = {
	eventName: '',
	eventType: 'Other',
	eventTypeOther: '',
	arrivalDate: null,
	departureDate: null,
	alternateDates: [],
	expectedPeakAttendees: 0,
	maxRoomRate: 0,
	hotelStarRating: '',
	desiredBrands: '',
	locations: { states: [], regions: [], countries: [], cities: [], hotels: [], locationNotes: '', avoidSourcingNotes: '' },
	programDays: [],
	openToAltArrival: false,
	altArrivalDays: [],
	datePatternNotes: ''
	,rfpUploadName: '',
	rfpUploadUrl: ''
};

const normalizeLocations = (locations = {}) => ({
	states: [],
	regions: [],
	countries: [],
	cities: [],
	hotels: [],
	locationNotes: '',
	avoidSourcingNotes: '',
	...locations,
});

class EventDetails extends Component {
	constructor(props) {
		super(props);
		const incoming = props.request?.eventDetails || {};
		this.state = {
			data: { ...DEFAULT, ...incoming, locations: normalizeLocations(incoming.locations) },
			requestName: props.request.name || '',
			editing: false,
			saving: false,
			uploadingRfpUpload: false
		};
	}

	getProgramDaysFromRange = (arrivalDate, departureDate) => {
		if (!arrivalDate || !departureDate) return [];

		const start = moment(arrivalDate);
		const end = moment(departureDate);
		if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return [];

		const programDays = [];
		const seenDays = new Set();
		const current = start.clone();

		while (current.isSameOrBefore(end, 'day')) {
			const day = current.format('ddd');
			if (!seenDays.has(day)) {
				seenDays.add(day);
				programDays.push(day);
			}
			current.add(1, 'days');
		}

		return programDays;
	};

	syncProgramDaysToDates = (arrivalDate, departureDate) => {
		this.setState((s) => ({
			data: { ...s.data, programDays: this.getProgramDaysFromRange(arrivalDate, departureDate) },
			editing: true
		}));
	};

	update = (field, value) => {
		this.setState((s) => ({
			data: { ...s.data, [field]: field === 'locations' ? normalizeLocations(value) : value },
			editing: true
		}));
	};

	addAlt = () => {
		this.setState((s) => ({
			data: {
				...s.data,
				alternateDates: [...(s.data.alternateDates || []), { arrivalDate: null, departureDate: null }]
			},
			editing: true
		}));
	};

	updateAlt = (i, field, value) => {
		this.setState((s) => {
			const alts = [...(s.data.alternateDates || [])];
			alts[i] = { ...alts[i], [field]: value };
			return { data: { ...s.data, alternateDates: alts }, editing: true };
		});
	};

	removeAlt = (i) => {
		this.setState((s) => {
			const alts = [...(s.data.alternateDates || [])];
			alts.splice(i, 1);
			return { data: { ...s.data, alternateDates: alts }, editing: true };
		});
	};

	toggleDay = (field, day) => {
		this.setState((s) => {
			const days = [...(s.data[field] || [])];
			const idx = days.indexOf(day);
			if (idx >= 0) days.splice(idx, 1);
			else days.push(day);
			return { data: { ...s.data, [field]: days }, editing: true };
		});
	};

	save = async () => {
		this.setState({ saving: true });
		try {
			const payload = {
				...this.state.data,
				locations: normalizeLocations(this.state.data.locations),
			};
			await API().patch(
				`Organizations/${this.props.orgId}/proposal-requests/${this.props.requestId}`,
				{
					eventDetails: payload,
					name: this.state.requestName
				}
			);
			this.setState({ editing: false, saving: false });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		} catch (e) {
			console.error('Error saving event details:', e);
			this.setState({ saving: false });
		}
	};

	cancel = () => {
		const incoming = this.props.request?.eventDetails || {};
		this.setState({
			data: { ...DEFAULT, ...incoming, locations: normalizeLocations(incoming.locations) },
			requestName: this.props.request.name || '',
			editing: false
		});
	};

	render() {
		const { data, requestName, editing, saving } = this.state;
		const { request } = this.props;

		const arrival   = data.arrivalDate   ? moment(data.arrivalDate).format('YYYY-MM-DD')   : '';
		const departure = data.departureDate ? moment(data.departureDate).format('YYYY-MM-DD') : '';

		return (
			<>
				{/* ── Basic info ──────────────────────────────── */}
				<div className="pr-field-group">
					<label className="pr-field-label">Request Name</label>
					<input
						className="pr-text-input large"
						type="text"
						value={requestName}
						onChange={(e) => this.setState({ requestName: e.target.value, editing: true })}
						placeholder="Annual Leadership Summit 2025"
					/>
				</div>

				<div className="pr-field-group">
					<label className="pr-field-label">Event Name</label>
					<input
						className="pr-text-input large"
						type="text"
						value={data.eventName || ''}
						onChange={(e) => this.update('eventName', e.target.value)}
						placeholder="Annual Leadership Summit 2025"
					/>
				</div>

				<div className="pr-field-group">
					<label className="pr-field-label">Event Type</label>
					<div className="pr-option-grid">
						{EVENT_TYPES.map((t) => (
							<button
								key={t.value}
								type="button"
								className={`pr-option-card ${data.eventType === t.value ? 'selected' : ''}`}
								onClick={() => this.update('eventType', t.value)}
							>
								<i className={t.icon} />
								<span>{t.value}</span>
							</button>
						))}
					</div>
				</div>

				{data.eventType === 'Other' && (
					<div className="pr-field-group">
						<label className="pr-field-label">Specify Event Type</label>
						<input
							className="pr-text-input"
							type="text"
							value={data.eventTypeOther || ''}
							onChange={(e) => this.update('eventTypeOther', e.target.value)}
							placeholder="Please specify"
						/>
					</div>
				)}

				{/* ── Dates ───────────────────────────────────── */}
				<h2 className="pr-subsection-title">Preferred Dates</h2>

				<div className="pr-row-2">
					<div className="pr-field-group">
						<label className="pr-field-label">Arrival Date</label>
						<div className="rfp-date-input-wrap">
							<DateInput
								className="rfp-date-input"
								value={arrival}
							onChange={(val) => {
								this.update('arrivalDate', val);
								if (val) {
									const newDeparture = moment(val).add(1, 'days').format('YYYY-MM-DD');
									this.setState((s) => ({
										data: { ...s.data, departureDate: newDeparture },
										editing: true
									}), () => {
										this.syncProgramDaysToDates(val, newDeparture);
									});
								}
							}}
						/>
						<i className="las la-calendar rfp-date-icon" />
					</div>
				</div>
				<div className="pr-field-group">
					<label className="pr-field-label">Departure Date</label>
					<div className="rfp-date-input-wrap">
						<DateInput
							className="rfp-date-input"
							value={departure}
							min={arrival}
							onChange={(val) => {
								this.update('departureDate', val);
								if (val) {
									this.syncProgramDaysToDates(arrival, val);
								}
							}}
							/>
							<i className="las la-calendar rfp-date-icon" />
						</div>
					</div>
				</div>

				<div className="pr-subsection-head">
					<h2 className="pr-subsection-title">Alternate Dates</h2>
					{(data.alternateDates || []).length > 0 && (
						<span className="pr-save-bar-meta">
							{data.alternateDates.length} alternate{data.alternateDates.length > 1 ? 's' : ''}
						</span>
					)}
				</div>

				{(data.alternateDates || []).length === 0 ? (
					<div className="pr-section-empty" style={{ marginBottom: 12 }}>
						<i className="las la-calendar-alt" />
						No alternate date options added yet.
					</div>
				) : (
					data.alternateDates.map((alt, i) => {
						const aArr = alt.arrivalDate   ? moment(alt.arrivalDate).format('YYYY-MM-DD')   : '';
						const aDep = alt.departureDate ? moment(alt.departureDate).format('YYYY-MM-DD') : '';
						return (
							<div key={i} className="pr-item-card">
								<div className="pr-item-card-header" style={{ cursor: 'default' }}>
									<h3 className="pr-item-title">
										<i className="las la-calendar" />
										Alternate option {i + 1}
									</h3>
									<button
										type="button"
										className="pr-icon-btn danger"
										onClick={() => this.removeAlt(i)}
										title="Remove"
									>
										<i className="las la-times" />
									</button>
								</div>
								<div className="pr-row-2">
									<div className="pr-field-group compact">
										<label className="pr-field-label">Arrival</label>
										<div className="rfp-date-input-wrap">
											<DateInput
												className="rfp-date-input"
												value={aArr}
												onChange={(val) => this.updateAlt(i, 'arrivalDate', val)}
											/>
											<i className="las la-calendar rfp-date-icon" />
										</div>
									</div>
									<div className="pr-field-group compact">
										<label className="pr-field-label">Departure</label>
										<div className="rfp-date-input-wrap">
											<DateInput
												className="rfp-date-input"
												value={aDep}
												min={aArr}
												onChange={(val) => this.updateAlt(i, 'departureDate', val)}
											/>
											<i className="las la-calendar rfp-date-icon" />
										</div>
									</div>
								</div>
							</div>
						);
					})
				)}

				<button type="button" className="pr-add-btn" onClick={this.addAlt}>
					<i className="las la-plus" />
					Add alternate dates
				</button>

				{/* ── Attendance & budget ─────────────────────── */}
				<h2 className="pr-subsection-title">Attendance &amp; Budget</h2>

				<div className="pr-field-group">
					<label className="pr-field-label">Expected Peak Attendees</label>
					<input
						className="pr-text-input large"
						type="number"
						min="0"
						value={data.expectedPeakAttendees || ''}
						onChange={(e) => this.update('expectedPeakAttendees', parseInt(e.target.value, 10) || 0)}
						placeholder="0"
					/>
				</div>

				<div className="pr-row-2">
					<div className="pr-field-group">
						<label className="pr-field-label">Desired Max Room Rate / Night</label>
						<div className="pr-currency-wrap">
							<span className="pr-currency-symbol">$</span>
							<input
								className="pr-text-input large"
								type="number"
								min="0"
								value={data.maxRoomRate || ''}
								onChange={(e) => this.update('maxRoomRate', parseFloat(e.target.value) || 0)}
								placeholder="0"
							/>
						</div>
					</div>
					<div className="pr-field-group">
						<label className="pr-field-label">Desired Hotel Star Rating</label>
						<input
							className="pr-text-input large"
							type="text"
							value={data.hotelStarRating || ''}
							onChange={(e) => this.update('hotelStarRating', e.target.value)}
							placeholder="e.g. 4-Star"
						/>
					</div>
				</div>

				<div className="pr-field-group">
					<label className="pr-field-label">Desired Brands / Hotel Type</label>
					<input
						className="pr-text-input"
						type="text"
						value={data.desiredBrands || ''}
						onChange={(e) => this.update('desiredBrands', e.target.value)}
						placeholder="e.g. Marriott, Hilton, boutique, lifestyle, resort…"
					/>
				</div>

				{/* ── Locations ───────────────────────────────── */}
				<h2 className="pr-subsection-title">Preferred Locations</h2>

				<LocationsStep
					hideTitle
					data={data}
					onChange={this.update}
				/>

				{/* ── Program Pattern ──────────────────────────── */}
				<h2 className="pr-subsection-title">Program Pattern</h2>

				<div className="pr-field-group">
					<label className="pr-field-label">
						Preferred Arrival Date(s)
						<span className="pr-field-hint">Auto-calculated from dates</span>
					</label>
					<div className="pr-day-selector">
						{DAYS_OF_WEEK.map((day) => {
							const programDays = this.getProgramDaysFromRange(data.arrivalDate, data.departureDate);
							return (
								<button
									key={day}
									type="button"
									className={`pr-day-btn ${programDays.includes(day) ? 'active' : ''} disabled-day`}
									disabled
								>
									{day}
								</button>
							);
						})}
					</div>
					{(() => {
						const programDays = this.getProgramDaysFromRange(data.arrivalDate, data.departureDate);
						return programDays.length > 0 && (
							<p className="pr-field-sublabel" style={{ marginTop: 8 }}>
								{programDays.length}-day program · arrives {programDays[0]}
							</p>
						);
					})()}
				</div>

				<div className="pr-field-group">
					<button
						type="button"
						className={`pr-toggle ${data.openToAltArrival ? 'on' : ''}`}
						onClick={() => this.update('openToAltArrival', !data.openToAltArrival)}
					>
						<span className="pr-toggle-track" />
						<span>Open to alternative arrival day</span>
					</button>
				</div>

				{data.openToAltArrival && (() => {
					const programDays = this.getProgramDaysFromRange(data.arrivalDate, data.departureDate);
				const selectedAltArrivalDay = (data.altArrivalDays || [])[0];
				return (
					<div className="pr-field-group">
						<label className="pr-field-label">
							Alternative Arrival Day
							<span className="pr-field-hint">Select one alternative</span>
						</label>
						<div className="pr-day-selector">
							{DAYS_OF_WEEK.map((day) => (
								<button
									key={day}
									type="button"
									className={`pr-day-btn ${selectedAltArrivalDay === day ? 'active' : ''} ${programDays[0] === day ? 'disabled' : ''}`}
									onClick={() => {
										if (programDays[0] !== day) {
											const newAltDays = selectedAltArrivalDay === day ? [] : [day];
											this.setState((s) => ({
												data: { ...s.data, altArrivalDays: newAltDays },
												editing: true
											}));
										}
									}}
							>
								{day}
							</button>
								))}
							</div>
						</div>
					);
				})()}

				<div className="pr-field-group">
					<label className="pr-field-label">Date &amp; Pattern Notes</label>
					<p className="pr-field-sublabel">
						Any additional notes about your dates, arrival/departure pattern, or flexibility.
					</p>
					<textarea
						className="pr-textarea"
						rows={4}
						value={data.datePatternNotes || ''}
						onChange={(e) => this.update('datePatternNotes', e.target.value)}
						placeholder="e.g. Prefer Sunday arrival, no blackout dates in March, open to +/- 1 day…"
					/>

					<div className="editorFileInput" style={{ marginTop: 16 }}>
						<FileChooserPreview
							label="RFP Upload"
							value={data.rfpUploadUrl || ''}
							fileName={data.rfpUploadName || ''}
							showPreview={false}
							clearFile={() => this.setState((s) => ({
								data: { ...s.data, rfpUploadName: '', rfpUploadUrl: '' },
								editing: true
							}))}
							onClick={() => this.setState({ uploadingRfpUpload: true })}
						/>
					</div>
				</div>
				{this.state.uploadingRfpUpload && (
							<FilesUploaderModal
								title="Account File"
								links={{
									getAssets: `Organizations/${this.props.orgId}/assets`,
									addAssets: `Organizations/${this.props.orgId}/assets`,
								}}
								isOpen={this.state.uploadingRfpUpload}
								toggle={() => this.setState({ uploadingRfpUpload: false })}
								orgId={this.props.orgId}
								addFiles={(fileURL, fileName) => {
									this.setState((s) => ({
										data: { ...s.data, rfpUploadUrl: fileURL, rfpUploadName: fileName },
										uploadingRfpUpload: false,
										editing: true
									}));
								}}
							/>
						)}

				

				<SaveBar
					editing={editing}
					saving={saving}
					onSave={this.save}
					onCancel={this.cancel}
					updatedAt={request?.updatedAt}
				/>
			</>
		);
	}
}

export default EventDetails;
